import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useBudgetAssistantStore } from '@flows.cash/core'
import { useAuthStore } from '@/store/authStore'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Home, Zap, ShoppingCart, Heart, CreditCard, Info, TrendingUp } from 'lucide-react'

interface FixedExpensesStepProps {
  onNext: () => void
  onSkip: () => void
}

interface ExpenseItem {
  id: string
  label: string
  icon: React.ReactNode
  description: string
  amount: string
}

export function FixedExpensesStep({ onNext, onSkip }: FixedExpensesStepProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { responses, updateResponses } = useBudgetAssistantStore()
  
  const [expenses, setExpenses] = useState<ExpenseItem[]>([
    {
      id: 'rentHousing',
      label: t('budgetAssistant.fixedExpenses.rent.question', 'How much do you pay monthly for rent or housing?'),
      icon: <Home className="h-5 w-5 text-blue-600" />,
      description: t('budgetAssistant.fixedExpenses.rent.description', 'This includes rent, mortgage payments, property taxes, or any housing costs you pay every month'),
      amount: ''
    },
    {
      id: 'utilities',
      label: t('budgetAssistant.fixedExpenses.utilities.question', 'What is your monthly utility bill estimate?'),
      icon: <Zap className="h-5 w-5 text-yellow-600" />,
      description: t('budgetAssistant.fixedExpenses.utilities.description', 'Electricity, water, gas, internet, phone bills - all the services that keep your home running'),
      amount: ''
    },
    {
      id: 'groceries',
      label: t('budgetAssistant.fixedExpenses.groceries.question', 'How much do you spend on groceries and essentials per month?'),
      icon: <ShoppingCart className="h-5 w-5 text-green-600" />,
      description: t('budgetAssistant.fixedExpenses.groceries.description', 'Food, cleaning supplies, toiletries, and other things you need regularly from the store'),
      amount: ''
    },
    {
      id: 'healthcare',
      label: t('budgetAssistant.fixedExpenses.healthcare.question', 'Do you have monthly health insurance or medical expenses?'),
      icon: <Heart className="h-5 w-5 text-red-600" />,
      description: t('budgetAssistant.fixedExpenses.healthcare.description', 'Health insurance premiums, regular medications, or any medical costs you pay monthly'),
      amount: ''
    },
    {
      id: 'loans',
      label: t('budgetAssistant.fixedExpenses.loans.question', 'Do you have any loan or installment payments?'),
      icon: <CreditCard className="h-5 w-5 text-purple-600" />,
      description: t('budgetAssistant.fixedExpenses.loans.description', 'Car payments, student loans, credit card minimums, or any money you borrowed that you pay back monthly'),
      amount: ''
    }
  ])

  const [hasAnyExpense, setHasAnyExpense] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Load existing responses into local state
  useEffect(() => {
    setExpenses(prevExpenses => prevExpenses.map(expense => {
      const savedAmount = responses[expense.id as keyof typeof responses] as number
      return {
        ...expense,
        amount: savedAmount ? savedAmount.toString() : ''
      }
    }))
  }, [responses])

  useEffect(() => {
    const hasExpenses = expenses.some(expense => expense.amount && parseFloat(expense.amount) > 0)
    setHasAnyExpense(hasExpenses)
  }, [expenses])

  const updateExpenseAmount = (id: string, amount: string) => {
    setExpenses(prev => prev.map(expense => 
      expense.id === id ? { ...expense, amount } : expense
    ))
  }

  const handleNext = async () => {
    if (user?.id) {
      setIsLoading(true)
      try {
        const expenseData = expenses.reduce((acc, expense) => {
          if (expense.amount && parseFloat(expense.amount) > 0) {
            acc[expense.id as keyof typeof acc] = parseFloat(expense.amount)
          }
          return acc
        }, {} as Record<string, number>)
        
        await updateResponses(expenseData, 'fixed_expenses')
        onNext()
      } catch (error) {
        console.error('Error saving fixed expenses data:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const getTotalExpenses = () => {
    return expenses.reduce((total, expense) => {
      const amount = parseFloat(expense.amount) || 0
      return total + amount
    }, 0)
  }

  const totalExpenses = getTotalExpenses()

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="relative mx-auto w-14 h-14 mb-3">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-3 shadow-lg">
            <Home className="h-8 w-8 text-white" />
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {t('budgetAssistant.fixedExpenses.title', 'Let\'s look at your regular monthly bills')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {t('budgetAssistant.fixedExpenses.subtitle', 'These are the bills you pay every month that are usually the same amount. Don\'t worry if you don\'t know the exact amounts - your best guess is fine!')}
        </p>
      </div>

      {/* Expenses Form */}
      <div className="space-y-3">
        {expenses.map((expense) => (
          <Card key={expense.id} className="p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800">
                {expense.icon}
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                    {expense.label}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {expense.description}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                      $
                    </div>
                    <Input
                      id={expense.id}
                      type="number"
                      placeholder="0.00"
                      value={expense.amount}
                      onChange={(e) => updateExpenseAmount(expense.id, e.target.value)}
                      className="pl-8 h-10 text-sm w-32"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <span className="text-xs text-gray-500">per month</span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Total Summary */}
      {totalExpenses > 0 && (
        <Card className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-blue-900 dark:text-blue-100 text-sm">
                {t('budgetAssistant.fixedExpenses.total.title', 'Great! Your monthly bills add up to:')}
              </h4>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                {t('budgetAssistant.fixedExpenses.total.description', 'This is what you need to cover your essential expenses each month')}
              </p>
            </div>
            <div className="text-xl font-bold text-blue-800 dark:text-blue-200">
              ${totalExpenses.toLocaleString()}
            </div>
          </div>
        </Card>
      )}

      {/* Budget Insights */}
      {totalExpenses > 0 && responses.monthlyIncome && (
        <Card className="p-5 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20 border-emerald-200 dark:border-emerald-800">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            <h4 className="font-medium text-emerald-900 dark:text-emerald-100 text-sm">
              {t('budgetAssistant.fixedExpenses.insights.title', 'Budget Analysis')}
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-emerald-800 dark:text-emerald-200 font-medium text-sm">
                {((totalExpenses / responses.monthlyIncome) * 100).toFixed(0)}% of income
              </div>
              <div className="text-emerald-600 dark:text-emerald-400 text-xs">
                {t('budgetAssistant.fixedExpenses.insights.percentage', 'Goes to fixed expenses')}
              </div>
            </div>
            <div>
              <div className="text-emerald-800 dark:text-emerald-200 font-medium text-sm">
                ${(responses.monthlyIncome - totalExpenses).toLocaleString()}
              </div>
              <div className="text-emerald-600 dark:text-emerald-400 text-xs">
                {t('budgetAssistant.fixedExpenses.insights.remaining', 'Remaining for other expenses')}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Help Card */}
      <Card className="p-4 bg-gray-50/50 dark:bg-gray-950/20 border-gray-200 dark:border-gray-800">
        <div className="flex items-start gap-3">
          <Info className="h-4 w-4 text-gray-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1 text-sm">
              {t('budgetAssistant.fixedExpenses.help.title', 'Don\'t have some of these expenses?')}
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {t('budgetAssistant.fixedExpenses.help.description', 'That\'s totally fine! Just skip the ones that don\'t apply to you. We\'ll still create a great budget with what you do have.')}
            </p>
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4">
        <Button
          variant="outline"
          onClick={onSkip}
          className="text-gray-600 border-gray-300 hover:bg-gray-50 h-10"
        >
          {t('common.skip', 'Skip')}
        </Button>
        
        <Button
          onClick={handleNext}
          disabled={isLoading}
          className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-6 h-10"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            t('common.continue', 'Continue')
          )}
        </Button>
      </div>
    </div>
  )
} 